import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Select, Typography, Button, Tooltip, Col } from "antd";
import { Link, useHistory } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  FormOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { IRequirement } from "../Project";
import api from "../../services/api";
import ActivityStatus from "../../enums/ActivityStatus";
import ActivityStatusDate from "../../enums/ActivityStatusDate";
import UserAvatar from "../../components/UserAvatar";
import FormItem from "../../components/FormItem";
import { removeNullKeys } from "../../utils/formatters";
import { IProject } from "../Projects";
import { useAuth } from "../../hooks/auth";
import HistoricActivityModal from "../FactoryActivity/HistoricActivityModal";
import ConcludeActivityModal from "../Activities/ConcludeActivityModal";
import filterOptions from "../../utils/filterOptions";
import {
  CardBoard,
  Container,
  Card,
  Title,
  Content,
  Description,
  Identifier,
  Footer,
  PageTitle,
  Page,
  CardScroll,
} from "./styled";
import { Label } from "../../components/ui";
import isWarningActivity from "../../utils/isWarningActivity";
import RemoveImpediment from "../Project/RemoveImpediment";
import IUserRoles from "../../interfaces/IUserRoles";
import IStatusImpediment from "../../interfaces/IStatusImpediment";

const sizeAvatar = 20;
const { Text } = Typography;

const Kanban = (): JSX.Element => {
  const [activities, setActivities] = useState<IRequirement[]>([]);
  const [selectActivity, setSelectActivity] = useState<
    IRequirement | undefined
  >(undefined);
  const [statusDev, setStatusDev] = useState<{ [key: string]: IRequirement[] }>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [projectCurrent, setProjectCurrent] = useState<IProject | undefined>(
    undefined,
  );
  const [projectID, setProjectID] = useState<string | undefined>(undefined);
  const [historicModalVisible, setHistoricModalVisible] = useState(false);
  const [concludeModalVisible, setConcludeModalVisible] = useState(false);
  const [removeImpedimentVisible, setRemoveImpedimentVisible] = useState(false);
  const [removeImpdementIds, setRemoveImplementIds] = useState<string[]>([]);
  const { user } = useAuth();
  const history = useHistory();

  const routeProjectID = window.location.pathname.split("/")[4];

  const isManagerOrAdministrative = useMemo(() => {
    return (
      user.role === IUserRoles.Manager ||
      user.role === IUserRoles.Administrative
    );
  }, [user.role]);

  const params = useMemo(() => {
    return {
      project_id: projectID,
    };
  }, [projectID]);

  function generateLabelProject(project: IProject): string {
    return `${project?.identifier ? `${project.identifier} - ` : ""}${
      project?.short_description
    }`;
  }

  useEffect(() => {
    setLoading(true);
    api
      .get<IRequirement[]>("/activities/by_projects", {
        params: removeNullKeys(params),
      })
      .then(response => {
        const statusDevCreate: { [key: string]: IRequirement[] } = {
          [ActivityStatusDate.NoStart]: [],
          [ActivityStatusDate.NoStartAndDelayed]: [],
          [ActivityStatusDate.StartOnTime]: [],
          [ActivityStatusDate.StartAndDelayed]: [],
          [ActivityStatusDate.Executed]: [],
        };

        function generateStatusDev(activity: IRequirement): void {
          const currentDateFormat = moment(moment().utc())
            .subtract(3, "hours")
            .format("YYYY-MM-DD");
          const beginDateFormat = moment(activity.expected_date_begin).format(
            "YYYY-MM-DD",
          );
          const endDateFormat = moment(activity.expected_date_end).format(
            "YYYY-MM-DD",
          );

          if (activity.status === ActivityStatus.Executed) {
            statusDevCreate[ActivityStatusDate.Executed].push(activity);
            return;
          }

          const isBeforeCurrentDate = moment(currentDateFormat).isSameOrBefore(
            endDateFormat,
          );

          const isAfterCurrentDate = moment(currentDateFormat).isSameOrAfter(
            beginDateFormat,
          );

          if (
            isBeforeCurrentDate &&
            isAfterCurrentDate &&
            !!activity?.date_begin_activity
          ) {
            statusDevCreate[ActivityStatusDate.StartOnTime].push(activity);
            return;
          }

          if (
            !isBeforeCurrentDate &&
            isAfterCurrentDate &&
            !!activity?.date_begin_activity
          ) {
            statusDevCreate[ActivityStatusDate.StartAndDelayed].push(activity);
            return;
          }

          if (
            !isBeforeCurrentDate &&
            isAfterCurrentDate &&
            !activity?.date_begin_activity
          ) {
            statusDevCreate[ActivityStatusDate.NoStartAndDelayed].push(
              activity,
            );
            return;
          }

          statusDevCreate[ActivityStatusDate.NoStart].push(activity);
        }

        const startProjectID = routeProjectID || response.data[0]?.project_id;

        const project_current = response.data.filter(
          act => act.project_id === startProjectID,
        )[0]?.project;

        setProjectCurrent(project_current);

        // eslint-disable-next-line array-callback-return
        response.data.map(activity => {
          if (activity.project_id === startProjectID) {
            generateStatusDev(activity);
          }
        });
        setStatusDev(statusDevCreate);
        setActivities(oldActivities => {
          if (oldActivities.length > 0) {
            return oldActivities;
          }
          return response.data;
        });
      })
      .finally(() => setLoading(false));
  }, [params, projectID, routeProjectID]);
  const usersProjects = useMemo(() => {
    if (!activities) {
      return undefined;
    }

    const setProject = new Set();
    return activities
      .map(act => {
        const project = ({
          ...act,
          identifier: act.project.identifier,
          short_description: act.project.short_description,
        } as unknown) as IProject;
        return {
          label: generateLabelProject(project),
          value: act.project_id,
        };
      })
      .filter(person => {
        const duplicatedProject = setProject.has(person.value);
        setProject.add(person.value);
        return !duplicatedProject;
      });
  }, [activities]);

  const beginActivity = useCallback(async (id: string) => {
    const date_begin_activity = moment(moment().utc()).format("YYYY-MM-DD");
    try {
      await api.patch(`/activities/${id}/update_activity`, {
        date_begin_activity,
      });
    } catch (error) {
      console.log(error);
    }
  }, []);

  const startActivity = useCallback(
    async (activity: IRequirement, preStatus: string, nextStatus: string) => {
      const nextStatusDev = { ...statusDev };
      nextStatusDev[preStatus] = nextStatusDev[preStatus].filter(
        act => act.id !== activity.id,
      );
      nextStatusDev[nextStatus].push(activity);
      setStatusDev(nextStatusDev);

      await beginActivity(activity.id);
    },
    [statusDev, beginActivity],
  );

  const handleCloseModal = (): void => {
    setHistoricModalVisible(false);
    setSelectActivity(undefined);
  };

  const handleCloseConcludeModal = (activity?: IRequirement): void => {
    if (activity) {
      const nextStatusDev = { ...statusDev };
      nextStatusDev[ActivityStatusDate.StartAndDelayed] = nextStatusDev[
        ActivityStatusDate.StartAndDelayed
      ].filter(act => act?.id !== activity.id);
      nextStatusDev[ActivityStatusDate.StartOnTime] = nextStatusDev[
        ActivityStatusDate.StartOnTime
      ].filter(act => act?.id !== activity.id);

      nextStatusDev[ActivityStatusDate.Executed].push(activity);
      setStatusDev(nextStatusDev);
    }

    setConcludeModalVisible(false);
    setSelectActivity(undefined);
  };

  const handleOpenModal = (activity: IRequirement): void => {
    setHistoricModalVisible(true);
    setSelectActivity(activity);
  };

  const modalRemoveImpedimentVisible = (activity: IRequirement): void => {
    setRemoveImpedimentVisible(true);
    setSelectActivity(activity);
  };

  const handleOpenModalConclude = (activity?: IRequirement): void => {
    setConcludeModalVisible(true);
    if (activity) {
      setSelectActivity(activity);
    }
  };

  const removeIconImpdiment = useCallback(
    (activity_id: string): boolean => {
      return removeImpdementIds.includes(activity_id);
    },
    [removeImpdementIds],
  );

  const handleCloseModalRemoveImpediment = (activity_id?: string): void => {
    if (activity_id) {
      setRemoveImplementIds(old => [...old, activity_id]);
    }
    setRemoveImpedimentVisible(false);
    setSelectActivity(undefined);
  };

  const viewDateActivity = (beginDate?: string, endDate?: string): string => {
    if (!beginDate || !endDate) {
      return "Data não informada";
    }
    return `${moment(beginDate).format("DD/MM/YYYY")} - ${moment(
      endDate,
    ).format("DD/MM/YYYY")}`;
  };

  if (loading && !activities.length) {
    return <SyncOutlined spin style={{ color: "orange" }} />;
  }

  if (!loading && !activities.length) {
    return <PageTitle>Nenhum Projeto Encontrado</PageTitle>;
  }

  return (
    <>
      {/* <>
        <Select
        options={}/>
      </> */}
      <RemoveImpediment
        visible={removeImpedimentVisible}
        handleClose={handleCloseModalRemoveImpediment}
        activity={selectActivity}
      />
      <HistoricActivityModal
        activity={selectActivity}
        visible={historicModalVisible}
        handleClose={handleCloseModal}
      />
      <ConcludeActivityModal
        activity={selectActivity}
        visible={concludeModalVisible}
        handleClose={handleCloseConcludeModal}
      />
      {routeProjectID ? (
        <Button
          onClick={() => history.goBack()}
          type="link"
          icon={<ArrowLeftOutlined />}
          size="large"
        >
          Voltar
        </Button>
      ) : (
        <></>
      )}
      <Page>
        <PageTitle>
          KANBAN{" "}
          <Link to={`/app/projects/${projectCurrent?.id}`}>
            {generateLabelProject(
              (projectCurrent as unknown) as IProject,
            ).toUpperCase()}
          </Link>
        </PageTitle>
        {!routeProjectID ? (
          <Col
            lg={12}
            md={12}
            sm={24}
            xl={9}
            xs={24}
            style={{
              padding: "0 24px",
              maxWidth: "400px",
            }}
          >
            <div>
              <Label color="#575962">Projetos</Label>
            </div>
            <FormItem>
              <Select
                showSearch
                options={usersProjects}
                onChange={e => setProjectID(e)}
                disabled={loading}
                defaultValue={generateLabelProject(
                  (projectCurrent as unknown) as IProject,
                )}
                filterOption={filterOptions}
              />
            </FormItem>
          </Col>
        ) : (
          <></>
        )}
        <Container>
          <CardScroll>
            <CardBoard>
              <Title>Não iniciado</Title>
              <Content>
                {statusDev[ActivityStatusDate.NoStart]?.map(activity => (
                  <Card key={activity.id}>
                    <div style={{ display: "flex" }}>
                      <Identifier>{activity?.identifier}</Identifier>
                      {!removeImpdementIds.includes(activity?.id) &&
                      isWarningActivity([activity]) ? (
                        <Tooltip title="Atividade com impedimento">
                          <Button
                            type="link"
                            style={{ marginTop: "-5px" }}
                            icon={<WarningOutlined />}
                            onClick={() => {
                              if (isManagerOrAdministrative) {
                                modalRemoveImpedimentVisible(activity);
                              }
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <></>
                      )}
                    </div>
                    <Description>{activity?.description}</Description>
                    <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                      <ClockCircleOutlined style={{ marginRight: 5 }} />
                      {viewDateActivity(
                        activity.expected_date_begin,
                        activity.expected_date_end,
                      )}
                    </Text>
                    <Footer>
                      <UserAvatar
                        url=""
                        name={activity?.consultant?.name || ""}
                        size={sizeAvatar}
                      />
                      {user.id === activity.consultant?.id ? (
                        <Tooltip title="Iniciar atividade">
                          <Button
                            type="link"
                            icon={<PlayCircleOutlined />}
                            onClick={
                              () =>
                                startActivity(
                                  activity,
                                  ActivityStatusDate.NoStart,
                                  ActivityStatusDate.StartOnTime,
                                )
                              // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                              // @ts-ignore
                            }
                          />
                        </Tooltip>
                      ) : (
                        <></>
                      )}
                    </Footer>
                  </Card>
                ))}
              </Content>
            </CardBoard>
            <CardBoard>
              <Title>Iniciado No horario</Title>
              <Content>
                {statusDev[ActivityStatusDate.StartOnTime]?.map(activity => (
                  <Card key={activity.id}>
                    <div style={{ display: "flex" }}>
                      <Identifier>{activity?.identifier}</Identifier>
                      {!removeImpdementIds.includes(activity?.id) &&
                      isWarningActivity([activity]) ? (
                        <Tooltip title="Atividade com impedimento">
                          <Button
                            type="link"
                            style={{ marginTop: "-5px" }}
                            icon={<WarningOutlined />}
                            onClick={() => {
                              if (isManagerOrAdministrative) {
                                modalRemoveImpedimentVisible(activity);
                              }
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <></>
                      )}
                    </div>
                    <Description>{activity?.description}</Description>
                    <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                      <ClockCircleOutlined style={{ marginRight: 5 }} />
                      {viewDateActivity(
                        activity.expected_date_begin,
                        activity.expected_date_end,
                      )}
                    </Text>
                    <Footer>
                      <UserAvatar
                        url=""
                        name={activity?.consultant?.name || ""}
                        size={sizeAvatar}
                      />
                      {user.id === activity.consultant?.id ? (
                        <div>
                          <Tooltip title="Descrição da Atividade">
                            <Button
                              type="link"
                              icon={<FormOutlined />}
                              onClick={() => handleOpenModal(activity)}
                            />
                          </Tooltip>
                          <Tooltip title="Concluir Atividade">
                            <Button
                              type="link"
                              icon={<CheckOutlined />}
                              onClick={() => handleOpenModalConclude(activity)}
                            />
                          </Tooltip>
                        </div>
                      ) : (
                        <></>
                      )}
                    </Footer>
                  </Card>
                ))}
              </Content>
            </CardBoard>
            <CardBoard>
              <Title>Não iniciado e Atrasada</Title>
              <Content>
                {statusDev[ActivityStatusDate.NoStartAndDelayed]?.map(
                  activity => (
                    <Card key={activity.id}>
                      <div style={{ display: "flex" }}>
                        <Identifier>{activity?.identifier}</Identifier>
                        {!removeImpdementIds.includes(activity?.id) &&
                        isWarningActivity([activity]) ? (
                          <Tooltip title="Atividade com impedimento">
                            <Button
                              type="link"
                              style={{ marginTop: "-5px" }}
                              icon={<WarningOutlined />}
                              onClick={() => {
                                if (isManagerOrAdministrative) {
                                  modalRemoveImpedimentVisible(activity);
                                }
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                      </div>
                      <Description>{activity?.description}</Description>
                      <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                        <ClockCircleOutlined style={{ marginRight: 5 }} />
                        {viewDateActivity(
                          activity.expected_date_begin,
                          activity.expected_date_end,
                        )}
                      </Text>
                      <Footer>
                        <UserAvatar
                          url=""
                          name={activity?.consultant?.name || ""}
                          size={sizeAvatar}
                        />
                        {user.id === activity.consultant?.id ? (
                          <Tooltip title="Iniciar atividade">
                            <Button
                              type="link"
                              icon={<PlayCircleOutlined />}
                              onClick={
                                () =>
                                  startActivity(
                                    activity,
                                    ActivityStatusDate.NoStartAndDelayed,
                                    ActivityStatusDate.StartAndDelayed,
                                  )
                                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                                // @ts-ignore
                              }
                            />
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                      </Footer>
                    </Card>
                  ),
                )}
              </Content>
            </CardBoard>
            <CardBoard>
              <Title>Iniciado E Atrasada</Title>
              <Content>
                {statusDev[ActivityStatusDate.StartAndDelayed]?.map(
                  activity => (
                    <Card key={activity.id}>
                      <div style={{ display: "flex" }}>
                        <Identifier>{activity?.identifier}</Identifier>
                        {!removeImpdementIds.includes(activity?.id) &&
                        isWarningActivity([activity]) ? (
                          <Tooltip title="Atividade com impedimento">
                            <Button
                              type="link"
                              style={{ marginTop: "-5px" }}
                              icon={<WarningOutlined />}
                              onClick={() => {
                                if (isManagerOrAdministrative) {
                                  modalRemoveImpedimentVisible(activity);
                                }
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                      </div>
                      <Description>{activity?.description}</Description>
                      <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                        <ClockCircleOutlined style={{ marginRight: 5 }} />
                        {viewDateActivity(
                          activity.expected_date_begin,
                          activity.expected_date_end,
                        )}
                      </Text>
                      <Footer>
                        <UserAvatar
                          url=""
                          name={activity?.consultant?.name || ""}
                          size={sizeAvatar}
                        />
                        {user.id === activity.consultant?.id ? (
                          <div>
                            <Tooltip title="Descrição da Atividade">
                              <Button
                                type="link"
                                icon={<FormOutlined />}
                                onClick={() => handleOpenModal(activity)}
                              />
                            </Tooltip>
                            <Tooltip title="Concluir Atividade">
                              <Button
                                type="link"
                                icon={<CheckOutlined />}
                                onClick={
                                  () => handleOpenModalConclude(activity)
                                  // ts-ignore
                                }
                              />
                            </Tooltip>
                          </div>
                        ) : (
                          <></>
                        )}
                      </Footer>
                    </Card>
                  ),
                )}
              </Content>
            </CardBoard>
            <CardBoard>
              <Title>Concluidas</Title>
              <Content>
                {statusDev[ActivityStatusDate.Executed]?.map(activity => (
                  <Card key={activity.id}>
                    <div style={{ display: "flex" }}>
                      <Identifier>{activity?.identifier}</Identifier>
                      {!removeImpdementIds.includes(activity?.id) &&
                      isWarningActivity([activity]) ? (
                        <Tooltip title="Atividade com impedimento">
                          <Button
                            type="link"
                            style={{ marginTop: "-5px" }}
                            icon={<WarningOutlined />}
                            onClick={() => {
                              if (isManagerOrAdministrative) {
                                modalRemoveImpedimentVisible(activity);
                              }
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <></>
                      )}
                    </div>
                    <Description>{activity?.description}</Description>
                    <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                      <ClockCircleOutlined style={{ marginRight: 5 }} />
                      {viewDateActivity(
                        activity.expected_date_begin,
                        activity.expected_date_end,
                      )}
                    </Text>
                    <Footer>
                      <UserAvatar
                        url=""
                        name={activity?.consultant?.name || ""}
                        size={sizeAvatar}
                      />
                    </Footer>
                  </Card>
                ))}
              </Content>
            </CardBoard>
          </CardScroll>
        </Container>
      </Page>
    </>
  );
};

export default Kanban;
